import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./FilmsPage.module.css";

/**
 * Screening Room — personal film appreciation archive.
 * Ten films I keep coming back to, ranked by how often I rewatch them
 * rather than any objective "greatness" — this is a shelf, not a canon.
 */
const FILMS = [
  {
    id: 1,
    title: "Whiplash",
    director: "Damien Chazelle",
    year: 2014,
    rating: 5,
    genre: "Music Drama",
    note: "The most inspiring movie for me — a redemption story for grinders.",
    quote: "I am doing it to prove that everyone can do it.",
  },
  {
    id: 2,
    title: "Little Miss Sunshine",
    director: "Jonathan Dayton & Valerie Faris",
    year: 2006,
    rating: 5,
    genre: "Family Comedy-Drama",
    note:
      "This movie made me cry more than once. Family is the last guardian angel that shields us from the world.",
    quote: "Oh, I wanna be a family man so bad TAT",
  },
  {
    id: 3,
    title: "Scent of a Woman",
    director: "Martin Brest",
    year: 1992,
    rating: 5,
    genre: "Drama",
    note: "A beautiful redemption story: when a man is loved, he protects and loves back.",
    quote: "But kinda gay … no?",
  },
  {
    id: 4,
    title: "The Banshees of Inisherin",
    director: "Martin McDonagh",
    year: 2022,
    rating: 5,
    genre: "Tragic Comedy",
    note:
      "A simple movie built from a simple story. As a gamer, I have to admit: this is proof that cinematic storytelling can't be replaced by any other medium.",
    quote:
      "I can't do that to my friends, though — why can't we just be friends? We're all gonna die anyway. Okay, maybe you want something higher, but at what cost?",
  },
  {
    id: 5,
    title: "Howl's Moving Castle",
    director: "Hayao Miyazaki",
    year: 2004,
    rating: 5,
    genre: "Animated Fantasy",
    note:
      "Miyazaki's films always pull me into a peaceful, beautiful world where everything slows down for a while.",
    quote: "火薬の火は嫌いだ。礼儀を知らない。",
  },
  {
    id: 6,
    title: "The Irishman",
    director: "Martin Scorsese",
    year: 2019,
    rating: 5,
    genre: "Crime Epic",
    note:
      "I ranked this so high partly because I don't respect how these old-guard actors — the soul of Hollywood — got shortchanged by the shift to modern streaming.",
    quote: "It is what it is.",
  },
  {
    id: 7,
    title: "Spider-Man: No Way Home",
    director: "Jon Watts",
    year: 2021,
    rating: 5,
    genre: "Superhero / Multiverse",
    note:
      "Yeah, it's a superhero movie and the plot doesn't really make sense. I still screamed when my Spider-Men showed up.",
    quote: "peak! for boys like me",
  },
  {
    id: 8,
    title: "Captain America: Civil War",
    director: "Anthony & Joe Russo",
    year: 2016,
    rating: 5,
    genre: "Superhero",
    note:
      "Another superhero movie, but it's great — go watch it. How did the Russo brothers handle Marvel movies this well? Please keep making those.",
    quote:
      "A billionaire playboy learned the necessity of discipline; a propaganda showcase of freedom found the real freedom. They just felt like LeetCode 160, Intersection of Two Linked Lists — so beautiful.",
  },
  {
    id: 9,
    title: "The Dark Knight",
    director: "Christopher Nolan",
    year: 2008,
    rating: 5,
    genre: "Superhero / Crime Thriller",
    note:
      "One of the greatest performances of the 21st century: Heath Ledger, the good-looking sweet-boy type, showed the world he could pull that off.",
    quote: "Why so seriouuuusss",
  },
  {
    id: 10,
    title: "The Lord of the Rings Trilogy",
    director: "Peter Jackson",
    year: 2003,
    rating: 5,
    genre: "Epic Fantasy",
    note:
      "Who can even compete with the scale of this? It's exactly why I built Backlot — I hope one day everyone can bring something like this out of their own heads.",
    quote: "Not all those who wander are lost.",
  },
];

function Stars({ count }) {
  return (
    <span className={styles.stars} aria-label={`${count} out of 5`}>
      {"★".repeat(count)}{"☆".repeat(5 - count)}
    </span>
  );
}

export default function FilmsPage() {
  const [active, setActive] = useState(FILMS[0]);

  return (
    <div className={styles.page}>
      <Link className="noir-back-portals" to="/noir-uni?skip=intro">← Noir Universe</Link>

      <header className={styles.masthead}>
        <p className={styles.kicker}>ACT II</p>
        <h1 className={styles.title}>Screening Room</h1>
        <p className={styles.subtitle}>Ten films I keep coming back to — the ones that shaped how I watch everything else</p>
        <div className={styles.rule} />
      </header>

      <main className={styles.main}>
        {/* ── Left: film list ──────────────────────────────── */}
        <nav className={styles.filmList} aria-label="Film list">
          {FILMS.map((film) => (
            <button
              key={film.id}
              className={`${styles.filmRow}${active.id === film.id ? ` ${styles.filmRowActive}` : ""}`}
              onClick={() => setActive(film)}
              aria-pressed={active.id === film.id}
            >
              <span className={styles.filmYear}>{film.year}</span>
              <span className={styles.filmTitle}>{film.title}</span>
              <span className={styles.filmGenreBadge}>{film.genre}</span>
            </button>
          ))}
        </nav>

        {/* ── Right: selected film detail ───────────────────── */}
        <article className={styles.detail} key={active.id}>
          <div className={styles.detailHeader}>
            <p className={styles.detailGenre}>{active.genre}</p>
            <h2 className={styles.detailTitle}>{active.title}</h2>
            <p className={styles.detailMeta}>
              {active.director} · {active.year}
            </p>
            <Stars count={active.rating} />
          </div>
          <div className={styles.detailDivider} />
          <div className={styles.noteWrap}>
            <blockquote className={styles.detailNote}>{active.note}</blockquote>
            {active.quote ? <p className={styles.detailQuote}>{active.quote}</p> : null}
          </div>
          <p className={styles.detailCue}>— Personal note</p>
        </article>
      </main>

      <p className={styles.footer}>REEL CHANGE.</p>
    </div>
  );
}
